/**
 * OrderShippingFeeWidget component.
 *
 * A toggle (styled like a plain boolean field) that, when enabled, shows a
 * number input next to it to enter the shipping fee amount. The value is
 * stored as a JSON string: {"enabled": boolean, "amount": number}.
 *
 * Lays out like a normal text input widget (a 4 wide column for the label —
 * here the toggle — and an 8 wide column for the input), rather than using
 * FormFieldWrapper's own label column, since the toggle itself needs to sit
 * where the label normally would.
 *
 * @module components/Widgets/OrderShippingFeeWidget/OrderShippingFeeWidget
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Form, Grid, Input, Label } from 'semantic-ui-react';
import cx from 'classnames';
import map from 'lodash/map';

// Parse the {"enabled", "amount"} JSON value, defaulting on any bad/old data.
const parseValue = (value) => {
  try {
    const parsed = JSON.parse(value);
    return {
      enabled: !!parsed?.enabled,
      amount: parsed?.amount ?? '',
    };
  } catch (error) {
    return { enabled: false, amount: '' };
  }
};

const OrderShippingFeeWidget = (props) => {
  const {
    id,
    title,
    description,
    required,
    error,
    value,
    onChange,
    isDisabled,
    className,
  } = props;
  const { enabled, amount } = parseValue(value);

  const update = (updates) => {
    onChange(id, JSON.stringify({ enabled, amount, ...updates }));
  };

  const onToggle = (event, { checked }) => {
    update({ enabled: checked });
  };

  const onAmountChange = (event) => {
    // Numbers only (digits + a single decimal point).
    const cleaned = event.target.value.replace(/[^0-9.]/g, '');
    update({ amount: cleaned });
  };

  return (
    <Form.Field
      inline
      required={required}
      error={error && error.length > 0}
      className={cx(
        description ? 'help' : '',
        className,
        `field-wrapper-${id}`,
      )}
    >
      <Grid>
        <Grid.Row stretched>
          <Grid.Column width="4">
            <div className="wrapper">
              <Checkbox
                id={`field-${id}`}
                name={`field-${id}`}
                checked={enabled}
                disabled={isDisabled}
                onChange={onToggle}
                label={<label htmlFor={`field-${id}`}>{title}</label>}
              />
            </div>
          </Grid.Column>
          <Grid.Column width={8}>
            {enabled && (
              <Input
                id={`field-${id}-amount`}
                name={`${id}-amount`}
                className="underline-input amount-input"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Amount"
                value={amount}
                disabled={isDisabled}
                onChange={onAmountChange}
              />
            )}
            {map(error, (message) => (
              <Label
                key={message}
                basic
                color="red"
                className="form-error-label"
              >
                {message}
              </Label>
            ))}
          </Grid.Column>
        </Grid.Row>
        {description && (
          <Grid.Row stretched>
            <Grid.Column stretched width="12">
              <p className="help">{description}</p>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </Form.Field>
  );
};

OrderShippingFeeWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  isDisabled: PropTypes.bool,
};

OrderShippingFeeWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  onChange: null,
  className: null,
  isDisabled: false,
};

export default OrderShippingFeeWidget;
